/* Quiz question docs are inconsistent across generators: some carry
   `correct_index` (number), others `correct_answer` / `answer` (option text
   or a letter). Resolve the correct option index from whatever is present. */
export function correctIndexOf(q) {
  if (Number.isInteger(q?.correct_index)) return q.correct_index;
  if (Number.isInteger(q?.correct)) return q.correct;
  const raw = q?.answer ?? q?.correct_answer;
  if (Number.isInteger(raw)) return raw;
  if (typeof raw === "string" && q?.options?.length) {
    const t = raw.trim().toLowerCase();
    const i = q.options.findIndex((o) => String(o).trim().toLowerCase() === t);
    if (i >= 0) return i;
    const letter = t.replace(/[.)\s]/g, "");
    if (letter.length === 1 && letter >= "a" && letter <= "f") return letter.charCodeAt(0) - 97;
  }
  return -1;
}

/* Lenient fill-blank answer matching — case-insensitive, punctuation-blind,
   singular/plural tolerant ("campaign" ≈ "campaigns"), word-containment
   ("custom audience" ≈ "custom") and 1-typo tolerance for longer words. */
const _norm = (s) => String(s ?? "").toLowerCase().trim().replace(/[.,!?;:'"“”‘’()\-_/]/g, " ").replace(/\s+/g, " ").trim();
const _singular = (w) => {
  if (w.length > 4 && w.endsWith("ies")) return w.slice(0, -3) + "y";
  if (w.length > 3 && w.endsWith("es")) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith("s")) return w.slice(0, -1);
  return w;
};
const _canon = (s) => _norm(s).split(" ").filter(Boolean).map(_singular).join(" ");
function _editDistLe1(a, b) {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0, j = 0, edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else { i++; j++; }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}
export function lenientMatch(got, expected) {
  const g = _canon(got), e = _canon(expected);
  if (!g || !e) return false;
  if (g === e) return true;
  const gw = g.split(" "), ew = e.split(" ");
  const gSet = new Set(gw), eSet = new Set(ew);
  if (ew.every((w) => gSet.has(w)) || gw.every((w) => eSet.has(w))) return true;
  if (gw.length === 1 && ew.length === 1 && Math.max(g.length, e.length) > 4) return _editDistLe1(g, e);
  return false;
}
